import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENV_CONFIG } from '../../../config/environment';
import { useTranslation } from '../../i18n/hooks';
import Button from '../../ui/components/Button';
import NotificationService from '../services/NotificationService';
import './TelegramDashboardBanner.css';

const DISMISS_KEY = 'telegramDashboardBanner_dismissed';

/**
 * TelegramDashboardBanner - Prompts subscribed users without Telegram to connect from Profile
 * @returns {JSX.Element|null} Dashboard banner or null when hidden
 */
const TelegramDashboardBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const featureEnabled = ENV_CONFIG.features.telegramNotifications;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true'
  );
  const [isConnected, setIsConnected] = useState(null);

  useEffect(function() {
    mountedRef.current = true;
    return function() {
      mountedRef.current = false;
    };
  }, []);

  useEffect(function() {
    if (!featureEnabled || dismissed) {
      return undefined;
    }

    let cancelled = false;

    const loadConnectionStatus = async function() {
      const { data, error } = await NotificationService.getSettings();
      if (cancelled || !mountedRef.current) {
        return;
      }

      if (error) {
        setIsConnected(true);
        return;
      }

      setIsConnected(Boolean(data?.connected));
    };

    loadConnectionStatus();

    return function() {
      cancelled = true;
    };
  }, [featureEnabled, dismissed]);

  const handleDismiss = function() {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, 'true');
  };

  if (!featureEnabled || dismissed || isConnected !== false) {
    return null;
  }

  return (
    <div className="telegram-dashboard-banner" role="banner">
      <div className="telegram-dashboard-banner__content">
        <div className="telegram-dashboard-banner__body">
          <p className="telegram-dashboard-banner__title">{t('telegramDashboardBanner.title')}</p>
          <p className="telegram-dashboard-banner__text">{t('telegramDashboardBanner.text')}</p>
        </div>
        <div className="telegram-dashboard-banner__actions">
          <Button
            variant="primary"
            size="small"
            onClick={() => navigate('/profile')}
          >
            {t('telegramDashboardBanner.cta')}
          </Button>
          <button
            className="telegram-dashboard-banner__dismiss"
            onClick={handleDismiss}
            aria-label={t('telegramDashboardBanner.dismiss')}
            type="button"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelegramDashboardBanner;
